import { mainLogger as log } from "@/app/logger"
import { useAppData } from "@/app/providers/app_data.provider"
import { useSelector } from "@/app/providers/selector_provider"
import { Interval, Term } from "@/app/types"
import { isNull, termId } from "@/app/utils"
import { Button } from "@/components/buttons/button"
import {
  getRankingTermOptions,
  getTermOptionKey,
} from "@/components/buttons/term_selector/button.term_selector_functions"
import { useMemo } from "react"

export type RankingTermSelectorButtonProps = {
  term: Term
  onClick: (newTerm: Term) => void
  isTermRelevant: boolean
  className?: string
}

/**
 * This component renders a button that allows users to select a term from a list of options.
 * It is specifically designed for displaying ranking values.
 *
 * @param term - The current term selected.
 * @param onClick - A callback function to be called when a new term is selected.
 * @param isTermRelevant - Indicates whether the term is relevant or not.
 * @param className - An optional className to be applied to the button.
 *a
 * @returns A React component that renders the RankingTermSelectorButton.
 */
export function RankingTermSelectorButton({
  term,
  onClick,
  isTermRelevant,
  className,
}: RankingTermSelectorButtonProps) {
  const { metaTerms, lookup } = useAppData()
  const { showSelector } = useSelector()

  const isTermAnnual = term.interval === Interval.Annual

  const termOptions = useMemo(
    () => getRankingTermOptions(term, metaTerms, isTermRelevant, false),
    [isTermRelevant, term]
  )

  log.debug("Options for Terms in Comparison Group List", termOptions)

  const pillText = useMemo(
    () =>
      isTermRelevant || isTermAnnual ?
        term.name[0]
      : metaTerms.ranking.names[term.interval][0],
    [term, isTermRelevant]
  )

  if (!termOptions) {
    log.debug("Still loading the terms/lookup. Not rendering Term Pills")
    return null
  }

  if (!term) {
    log.debug("No term selected, so cannot render Term Pills")
    return null
  }

  const handleSelect = (newTermKey: string, newInterval: string) => {
    if (isNull(newTermKey)) return // user closed selector menu and didnt choose option

    if (!newInterval) newInterval = term.interval // in case we show only one interval in selector (ranking values viewed from periodic chart selected)

    const newTermId = termId(false, newInterval, newTermKey)

    if (newTermId !== termId(false, term.interval, term.key)) {
      const newTerm = lookup.getTerm(
        false,
        Interval.get(newInterval),
        newTermKey
      )
      if (newTerm) onClick(newTerm) // call the handler
    }
  }

  const selector = {
    options: termOptions,
    selectedGroup: term.interval,
    selectedKey:
      isTermRelevant || isTermAnnual ?
        term.key
      : getTermOptionKey(termOptions, term),
    onSelect: handleSelect,
  }

  const shouldDisplayTextInsteadOfPill =
    termOptions.length === 1 && termOptions[0].items.length === 1

  return shouldDisplayTextInsteadOfPill ?
      <p className={`min-w-[8.5rem] text-center text-sm font-semibold`}>
        {pillText}
      </p>
    : <Button
        id="term-selector"
        color="tertiary"
        className={className}
        onClick={() => showSelector(selector)}>
        {pillText}
      </Button>
}
